import Loader from 'Components/Common/Loader';
import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { createCampaignFromIdea, fetchCampaignBrief, fetchCampaignBriefIdeas, fetchRecentBriefs, generateCampaignIdeasFromBrief } from 'services/api/campaign';
import { AppDispatch, RootState } from 'store';
import { CampaignBrief, CampaignIdea } from 'types/campaign';
import { getErrorMessage } from 'utils/helpers';
import '../../../pages/Campaigns/style.scss';
import { Link, useNavigate } from 'react-router-dom';
import { UserBrand } from 'types/users';
import { fetchUserBrands as fetchUserBrandsThunk } from 'slices/users/brands/thunk';
import AICampaignCreationLayout from 'Layouts/AICampaignCreationLayout';
import AICampaignIdeas from './AICampaignIdeas';
import Skeleton from 'react-loading-skeleton';
import { useFirebase } from 'contexts/FirebaseAuthContext';
import { collection, doc, limit, onSnapshot, orderBy, query } from 'firebase/firestore';
import { parse } from 'best-effort-json-parser';
import AILoader from 'Components/Loader/AILoader';
import PlatformSelectionModal from './PlatformSelectionModal';

const POLL_INTERVAL = 10000;

const CampaignCreationAIBrief: React.FC<{ brandSocialAccountId: string | number, onBrandChange?: (brand: UserBrand | null) => void }> = ({ brandSocialAccountId, onBrandChange }) => {
    const selectedAgencyId = useSelector((state: RootState) => state.auth.selectedAgencyId);
    const dispatch: AppDispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const [generatingIdeas, setGeneratingIdeas] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [ideasGeneratedAt, setIdeasGeneratedAt] = useState<string | null>(null);
    const [newIdeasAvailable, setNewIdeasAvailable] = useState(false);
    const [selectedIdea, setSelectedIdea] = useState<CampaignIdea | null>(null);
    const [fetchingIdeas, setFetchingIdeas] = useState(false);

    const [campaignIdeas, setCampaignIdeas] = useState<CampaignIdea[]>([]);
    const [creatingCampaign, setCreatingCampaign] = useState(false);
    const [showPlatformModal, setShowPlatformModal] = useState(false);
    const navigate = useNavigate();
    const { realtimeUpdatesDB } = useFirebase();
    const [notes, setNotes] = useState<string>('');
    const [recentPrompts, setRecentPrompts] = useState<CampaignBrief[]>([]);
    const [recentPromptsLoading, setRecentPromptsLoading] = useState(false);

    const [brief, setBrief] = useState<CampaignBrief | null>(null);

    const [aiTaskUUID, setAiTaskUUID] = useState<string | null>(null);
    const [aiTaskChipText, setAiTaskChipText] = useState<string | null>(null);
    const [aiTaskStatus, setAiTaskStatus] = useState<string | null>(null);
    const [aiTaskResult, setAiTaskResult] = useState<any | null>(null);
    const [newIdeasPartiallyAvailable, setNewIdeasPartiallyAvailable] = useState(false);


    useEffect(() => {
        dispatch(fetchUserBrandsThunk(false));
    }, [dispatch]);


    useEffect(() => {
        if (!aiTaskUUID || !realtimeUpdatesDB) return;

        if (aiTaskResult) {
            return;
        };

        const q = query(
            collection(realtimeUpdatesDB, "ai_tasks", aiTaskUUID, "events"),
            orderBy('updated_at', 'desc'),
            limit(1)
        );


        const unsubscribe = onSnapshot(q, (snapshot) => {
            const newMessages: any = snapshot.docs.map((doc) => ({
                id: doc.id,
                ...doc.data(),
            }));
            setAiTaskChipText(newMessages[0]?.event ?? null);
        });

        return () => unsubscribe();
    }, [aiTaskUUID, realtimeUpdatesDB, aiTaskResult]);

    useEffect(() => {
        if (!aiTaskUUID || !realtimeUpdatesDB) return;
        if (aiTaskStatus === 'completed') {
            setGeneratingIdeas(false);
            fetchIdeas();
            return;
        }
        setGeneratingIdeas(true);

        const unsubscribe = onSnapshot(
            doc(realtimeUpdatesDB, 'ai_tasks', aiTaskUUID),
            (snapshot) => {
                if (!snapshot.exists()) return;

                const data = snapshot.data();

                setAiTaskStatus(data?.status);
                setAiTaskResult(data?.result);
            },
            (error) => {
                console.error("doc listener error:", error);
            }
        );

        return () => unsubscribe();
    }, [aiTaskUUID, realtimeUpdatesDB, aiTaskStatus]);

    useEffect(() => {
        if (!aiTaskResult) return;
        const jsonResult = parse(aiTaskResult);
        const ideas = Array.isArray(jsonResult?.campaigns) ? jsonResult?.campaigns : [];
        setCampaignIdeas(ideas.map((i: CampaignIdea, index: number) => ({ uuid: index, campaign: i })));
        setNewIdeasPartiallyAvailable(ideas.length > 0);
    }, [aiTaskResult])

    const fetchRecentPrompts = async () => {
        if (!selectedAgencyId || !brandSocialAccountId) return;
        setRecentPromptsLoading(true);
        try {
            const response = await fetchRecentBriefs(selectedAgencyId, brandSocialAccountId);
            setRecentPrompts(response?.items ?? []);
        } catch (error) {
            toast.error(getErrorMessage(error, 'Failed to fetch recent briefs'));
        } finally {
            setRecentPromptsLoading(false);
        }
    }

    useEffect(() => {
        if (brandSocialAccountId && selectedAgencyId) {
            fetchRecentPrompts();
        }
    }, [brandSocialAccountId, selectedAgencyId]);


    const createCampaign = async (platform: string) => {
        if (!selectedIdea || !selectedAgencyId) return;
        setCreatingCampaign(true);
        try {
            const response = await createCampaignFromIdea(selectedAgencyId, selectedIdea.uuid, platform);
            if (response.campaign?.uuid) {
                navigate(`/campaign/${response.campaign.uuid}/edit`);
            }

        } catch (error) {
            toast.error(getErrorMessage(error, 'Failed to create campaign'));
        } finally {
            setCreatingCampaign(false);
            setShowPlatformModal(false);
        }

    }

    const fetchIdeas = async (campaignBrief: CampaignBrief | null = brief) => {
        if (!selectedAgencyId || !brandSocialAccountId || !campaignBrief) return;
        setFetchingIdeas(true);
        try {
            const response = await fetchCampaignBriefIdeas(selectedAgencyId, brandSocialAccountId, campaignBrief.uuid);
            setCampaignIdeas(response?.items ?? []);
            setNewIdeasAvailable(true);
        } catch (error) {
            toast.error(getErrorMessage(error, 'Failed to fetch campaign ideas'));
        } finally {
            setFetchingIdeas(false);
        }
    }

    const fetchBrief = async () => {
        if (!selectedAgencyId || !brandSocialAccountId || !brief?.uuid) return;
        try {
            const updatedBrief = await fetchCampaignBrief(selectedAgencyId, brandSocialAccountId, brief.uuid);
            if (updatedBrief && brief?.uuid === updatedBrief.uuid) {
                // console.log('updatedBrief', updatedBrief);
                // console.log('brief', brief);
                setBrief(updatedBrief);
                // console.log('new Date(updatedBrief.ideas_generated_at ?? 0).getTime()', new Date(updatedBrief.ideas_generated_at ?? 0).getTime());
                // console.log('new Date(brief.ideas_generated_at ?? 0).getTime()', new Date(brief.ideas_generated_at ?? 0).getTime());
                // console.log('new Date(updatedBrief.ideas_generated_at ?? 0).getTime() > new Date(brief.ideas_generated_at ?? 0).getTime()', new Date(updatedBrief.ideas_generated_at ?? 0).getTime() > new Date(brief.ideas_generated_at ?? 0).getTime());
                if ((new Date(updatedBrief.ideas_generated_at ?? 0).getTime()) > new Date(brief.ideas_generated_at ?? 0).getTime()) {
                    setNewIdeasAvailable(true);
                    setGeneratingIdeas(false);
                    // console.log('new ideas available');
                    // console.log('fetching ideas');
                    fetchIdeas();
                } else {
                    // console.log('no new ideas available');
                    setTimeout(() => { fetchBrief() }, POLL_INTERVAL);
                }
            }
        } catch (error) {
            toast.error(getErrorMessage(error, 'Failed to fetch campaign brief'));
        }
    }

    // useEffect(() => {
    //     if (generatingIdeas && brandSocialAccountId && selectedAgencyId) {
    //         setTimeout(() => { fetchBrief() }, POLL_INTERVAL);
    //     }
    // }, [generatingIdeas, brandSocialAccountId, selectedAgencyId])



    const generateIdeas = async () => {
        if (!notes || !selectedAgencyId || !brandSocialAccountId) return;
        setGenerating(true);
        try {
            const response = await generateCampaignIdeasFromBrief(selectedAgencyId, brandSocialAccountId, notes);
            setBrief(response?.brief ?? null);
            if (response.brief?.stream_uuid) {
                setAiTaskUUID(response.brief.stream_uuid);
            }
            setGeneratingIdeas(true);
        } catch (error) {
            toast.error(getErrorMessage(error, 'Failed to generate campaign ideas'));
            setGeneratingIdeas(false);
        } finally {
            setGenerating(false);
        }

    }

    const formatBriefDate = (createdAt?: string) => {
        if (!createdAt) return { date: '', time: '' };
        const d = new Date(createdAt);
        if (Number.isNaN(d.getTime())) return { date: '', time: '' };
        const date = d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
        const time = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }).replace(' ', '');
        return { date, time };
    };

    const handleRecentPromptClick = async (prompt: CampaignBrief) => {
        setBrief(prompt);
        setNewIdeasAvailable(true)
        fetchIdeas(prompt);
    }

    const resetState = (clearNotes: boolean = false) => {
        setNewIdeasAvailable(false);
        setBrief(null);
        if (clearNotes) {
            setNotes('');
        }
        setAiTaskUUID(null);
        setAiTaskChipText(null);
        setAiTaskStatus(null);
        setAiTaskResult(null);
        setSelectedIdea(null);
        setGeneratingIdeas(false);
        setGenerating(false);
        setFetchingIdeas(false);
        setCreatingCampaign(false);
        setShowPlatformModal(false);
        fetchRecentPrompts();
        setNewIdeasPartiallyAvailable(false);
    }

    const showCampaignIdeas = useMemo(() => {
        return newIdeasPartiallyAvailable || newIdeasAvailable;
    }, [newIdeasPartiallyAvailable, newIdeasAvailable]);

    if (generatingIdeas && !newIdeasAvailable && !newIdeasPartiallyAvailable) {
        return (
            <AILoader chipText={aiTaskChipText ?? 'Analysing with AI'} />
        );
    }

    if (loading) return <Loader />

    return (
        <AICampaignCreationLayout
            onBackClick={() => (showCampaignIdeas) ? resetState() : navigate('/campaign/new')}
            mode="prompt"
            steps={[
                {
                    label: 'Prompt',
                    title: 'Prompt your campaign',
                    subtitle: 'Tell us what you want your next campaign to look like—and how you want it to perform.',
                    active: !showCampaignIdeas,
                    completed: showCampaignIdeas,
                },
                {
                    label: 'Choose Campaign',
                    title: 'Choose a Campaign',
                    subtitle: 'Each campaign is a ready-to-deploy content brief aligned with this strategy.',
                    active: showCampaignIdeas,
                    completed: false,
                },
                {
                    label: 'Review',
                    title: 'Review',
                    subtitle: 'Review the campaign and make any final adjustments.',
                    active: false,
                    completed: false,
                }
            ]}
            brandSocialAccountId={brandSocialAccountId}
            onBrandChange={onBrandChange}
            bottomBarContent={
                (showCampaignIdeas) ? (
                    selectedIdea?.generated_campaign?.uuid ? (
                        <Link
                            className="btn btn-gradient"
                            to={`/campaign/${selectedIdea?.generated_campaign?.uuid}`}
                            target="_blank"
                        >
                            View Campaign
                        </Link>
                    ) :
                        (
                            <button
                                type="button"
                                className="btn btn-gradient"
                                disabled={!selectedIdea || creatingCampaign || !newIdeasAvailable}
                                onClick={() => setShowPlatformModal(true)}
                            >
                                {creatingCampaign ? 'CREATING CAMPAIGN...' : (generatingIdeas ? 'GENERATING IDEAS...' : 'USE THIS CAMPAIGN SETUP')}
                            </button>
                        )

                ) : (
                    <button
                        type="button"
                        className="btn btn-ai-gradient"
                        disabled={!notes || generating}
                        onClick={generateIdeas}
                    >
                        {generating ? 'PLEASE WAIT...' : 'GENERATE IDEAS'}
                    </button>
                )
            }
        >
            {
                showCampaignIdeas ? (
                    <AICampaignIdeas loading={fetchingIdeas} generating={generatingIdeas} campaignIdeas={campaignIdeas} onIdeaSelect={setSelectedIdea} />
                ) : (
                    <div className="campaign-ai-brief">
                        <textarea
                            className="form-control campaign-ai-creation__textarea campaign-ai-brief__textarea"
                            placeholder="Enter your prompt here"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                        />

                        <div className="campaign-ai-brief__recent">
                            <div className="campaign-ai-brief__recentTitle">RECENT PROMPTS</div>

                            <div className="campaign-ai-brief__recentScroll">
                                <div className="campaign-ai-brief__recentList" role="list" aria-label="Recent prompts">
                                    {recentPromptsLoading ? (
                                        Array.from({ length: 3 }).map((_, idx) => (
                                            <div key={idx} className="campaign-ai-brief__recentSkeleton" role="listitem" aria-hidden="true">
                                                <div className="campaign-ai-brief__recentSkeletonText">
                                                    <Skeleton height={16} width="85%" />
                                                    <Skeleton height={16} width="72%" style={{ marginTop: 10 }} />
                                                </div>
                                                <div className="campaign-ai-brief__recentSkeletonMeta">
                                                    <Skeleton height={14} width={84} />
                                                    <Skeleton height={16} width={70} style={{ marginTop: 8 }} />
                                                </div>
                                            </div>
                                        ))
                                    ) : (recentPrompts ?? []).length === 0 ? (
                                        <div className="campaign-ai-brief__recentEmpty">No recent prompts yet.</div>
                                    ) : (
                                        (recentPrompts ?? []).map((p) => {
                                            const { date, time } = formatBriefDate(p.created_at);
                                            const promptText = (p.ideation_notes ?? '').trim();
                                            return (
                                                <button
                                                    key={p.uuid}
                                                    type="button"
                                                    className="campaign-ai-brief__recentItem"
                                                    onClick={() => handleRecentPromptClick(p)}
                                                    role="listitem"
                                                    aria-label="Use this recent prompt"
                                                >
                                                    <div className="campaign-ai-brief__recentItemText">
                                                        {promptText || 'Untitled prompt'}
                                                    </div>
                                                    <div className="campaign-ai-brief__recentItemMeta" aria-hidden="true">
                                                        <div className="campaign-ai-brief__recentItemDate">{date}</div>
                                                        <div className="campaign-ai-brief__recentItemTime">{time}</div>
                                                    </div>
                                                </button>
                                            );
                                        })
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )
            }

            <PlatformSelectionModal
                isOpen={showPlatformModal}
                onClose={() => setShowPlatformModal(false)}
                onConfirm={createCampaign}
                processing={creatingCampaign}
            />
        </AICampaignCreationLayout>
    )
}

export default CampaignCreationAIBrief